import React, { createContext, useContext, useState } from 'react';
import { ThemeProvider as StyledThemeProvider, createGlobalStyle } from 'styled-components';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState({
    mode: 'dark',
    colors: {
      // iOS-like palette
      primary: '#0A84FF',
      secondary: '#8E8E93',
      success: '#32D74B',
      warning: '#FFD60A',
      danger: '#FF453A',
      background: '#0b0c10',
      backgroundGradient: 'linear-gradient(180deg, #0b0c10 0%, #0f1115 100%)',
      surface: '#111217',
      text: '#F2F4F7',
      textSecondary: '#A1A8B3',
      border: '#23252d',
      hover: '#151822'
    },
    radius: {
      sm: '8px',
      md: '12px',
      lg: '16px'
    },
    shadow: {
      sm: '0 1px 2px rgba(0,0,0,0.2)',
      md: '0 8px 20px rgba(0,0,0,0.25)'
    }
  });

  const toggleTheme = () => {
    setTheme(prev => ({
      ...prev,
      mode: prev.mode === 'dark' ? 'light' : 'dark',
      colors: prev.mode === 'dark' ? {
        ...prev.colors,
        background: '#F8F9FB',
        backgroundGradient: 'linear-gradient(180deg, #F8F9FB 0%, #F2F4F7 100%)',
        surface: '#FFFFFF',
        text: '#0B1220',
        textSecondary: '#475467',
        border: '#E5E7EB',
        hover: '#EEF2F7'
      } : {
        ...prev.colors,
        background: '#0b0c10',
        backgroundGradient: 'linear-gradient(180deg, #0b0c10 0%, #0f1115 100%)',
        surface: '#111217',
        text: '#F2F4F7',
        textSecondary: '#A1A8B3',
        border: '#23252d',
        hover: '#151822'
      }
    }));
  };

  const GlobalStyle = createGlobalStyle`
    html, body, #root { height: 100%; }
    * { box-sizing: border-box; }

    button {
      border-radius: ${props => props.theme.radius.md};
      transition: transform .06s ease, background-color .2s ease, box-shadow .2s ease;
    }
    button:active { transform: translateY(1px); }

    input, textarea, select {
      border-radius: ${props => props.theme.radius.md};
      outline: none;
      transition: box-shadow .2s ease, border-color .2s ease, background-color .2s ease;
    }
    input:focus, textarea:focus, select:focus {
      box-shadow: 0 0 0 3px rgba(10,132,255,0.25);
      border-color: #0A84FF;
    }
  `;

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <StyledThemeProvider theme={theme}>
        <GlobalStyle />
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};
